import { useCallback, useEffect, useRef, useState } from "react"

interface UseUnsavedChangesGuardResult {
  dirty: boolean
  markDirty: () => void
  markClean: () => void
  /**
   * Runs `action` immediately when nothing is unsaved; otherwise stores it and returns false
   * so the caller can show a confirmation.
   */
  guard: (action: () => void) => boolean
  /** Runs the action the last `guard` call deferred. */
  confirmPending: () => void
  cancelPending: () => void
  pending: boolean
}

/**
 * Tracks whether a form holds edits that have not been sent, and gates navigation on them.
 *
 * Settings tabs keep their edits in local state, so moving between tabs discards them with no
 * warning — indistinguishable, to an operator, from a save that did not stick.
 */
export function useUnsavedChangesGuard(): UseUnsavedChangesGuardResult {
  const [dirty, setDirty] = useState(false)
  const [pending, setPending] = useState(false)
  const pendingAction = useRef<(() => void) | null>(null)

  const markDirty = useCallback(() => setDirty(true), [])
  const markClean = useCallback(() => setDirty(false), [])

  const guard = useCallback(
    (action: () => void) => {
      if (!dirty) {
        action()
        return true
      }

      pendingAction.current = action
      setPending(true)
      return false
    },
    [dirty]
  )

  const confirmPending = useCallback(() => {
    pendingAction.current?.()
    pendingAction.current = null
    setPending(false)
    setDirty(false)
  }, [])

  const cancelPending = useCallback(() => {
    pendingAction.current = null
    setPending(false)
  }, [])

  // Closing the tab or reloading bypasses every in-app guard, so the browser has to ask.
  useEffect(() => {
    if (!dirty) return undefined

    const onBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault()
      event.returnValue = ""
    }

    window.addEventListener("beforeunload", onBeforeUnload)
    return () => window.removeEventListener("beforeunload", onBeforeUnload)
  }, [dirty])

  return { dirty, markDirty, markClean, guard, confirmPending, cancelPending, pending }
}
